import React, { useState, useEffect } from 'react';
import { Search, Download, X, ExternalLink, Loader } from 'lucide-react';
import toast from 'react-hot-toast';

const ImagePicker = ({ isOpen, onClose, onSelect, searchQuery = '', orientation = 'landscape', provider = 'unsplash' }) => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(searchQuery);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [activeProvider, setActiveProvider] = useState(provider);

  useEffect(() => {
    if (isOpen && searchQuery) {
      handleSearch(searchQuery, activeProvider);
    }
  }, [isOpen, searchQuery, activeProvider]);

  const handleProviderChange = (newProvider) => {
    setActiveProvider(newProvider);
    setPhotos([]);
    if (search.trim()) {
      handleSearch(search, newProvider);
    }
  };

  const handleSearch = async (query = search, searchProvider = activeProvider) => {
    if (!query.trim()) return;
    
    setLoading(true);
    try {
      const apiEndpoint = searchProvider === 'pexels' ? '/api/pexels/search' : '/api/unsplash/search';
      const response = await fetch(`${apiEndpoint}?query=${encodeURIComponent(query)}&orientation=${orientation}&count=24`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPhotos(data.photos || []);
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to search images');
      }
    } catch (error) {
      toast.error('Failed to search images');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPhoto = async (photo) => {
    try {
      // Trigger download for attribution compliance
      if (activeProvider === 'pexels') {
        // Pexels doesn't require download tracking, but for consistency
        console.log('Pexels photo selected:', photo.id);
      } else {
        // Trigger download for Unsplash attribution compliance
        await fetch(`/api/unsplash/download/${photo.id}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
      }

      onSelect({
        url: photo.url || photo.urls?.regular || photo.urls?.full,
        thumb: photo.thumb || photo.urls?.thumb || photo.urls?.small,
        alt: photo.alt || photo.alt_description || photo.description,
        credit: {
          photographer: photo.photographer,
          photographerUrl: photo.attribution?.photographerLink || photo.photographerProfileUrl || photo.photographer_url || photo.photographerUrl,
          unsplashUrl: photo.attribution?.unsplashLink || photo.unsplashUrl,
          provider: photo.provider || activeProvider
        }
      });
      
      onClose();
      toast.success('Image selected successfully');
    } catch (error) {
      console.error('Error selecting image:', error);
      toast.error('Failed to select image');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Choose an Image</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Provider Selection */}
          <div className="flex justify-center mb-4">
            <div className="bg-gray-100 rounded-lg p-1 flex">
              <button
                onClick={() => handleProviderChange('unsplash')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                  activeProvider === 'unsplash'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Unsplash
              </button>
              <button
                onClick={() => handleProviderChange('pexels')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                  activeProvider === 'pexels'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Pexels
              </button>
            </div>
          </div>
          
          {/* Search */}
          <div className="flex space-x-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Search for images..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={() => handleSearch()}
              disabled={loading || !search.trim()}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
            >
              {loading ? <Loader className="w-4 h-4 animate-spin" /> : 'Search'}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {loading && photos.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="w-8 h-8 animate-spin text-blue-600" />
              <span className="ml-3 text-gray-600">Searching for images...</span>
            </div>
          ) : photos.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {photos.map((photo) => (
                <div
                  key={photo.id}
                  className="group relative cursor-pointer rounded-lg overflow-hidden border border-gray-200 hover:border-blue-500 transition"
                  onClick={() => handleSelectPhoto(photo)}
                >
                  <div className="aspect-[4/3] bg-gray-200">
                    <img
                      src={photo.thumb || photo.urls?.thumb || photo.urls?.small}
                      alt={photo.alt || photo.alt_description || photo.description}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                    />
                  </div>
                  
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity">
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Download className="w-8 h-8 text-white" />
                    </div>
                  </div>
                  
                  {/* Credit */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-3">
                    <div className="text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                      Photo by {photo.photographer}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : search ? (
            <div className="text-center py-12">
              <div className="text-gray-500 mb-4">No images found for "{search}"</div>
              <button
                onClick={() => handleSearch('business')}
                className="text-blue-600 hover:underline"
              >
                Try searching for "business" instead
              </button>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              Enter a search term to find images
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <div className="flex items-center space-x-2">
              <span>Powered by</span>
              {activeProvider === 'pexels' ? (
                <a
                  href="https://www.pexels.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-purple-600 hover:underline flex items-center space-x-1"
                >
                  <span>Pexels</span>
                  <ExternalLink className="w-3 h-3" />
                </a>
              ) : (
                <a
                  href="https://unsplash.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline flex items-center space-x-1"
                >
                  <span>Unsplash</span>
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
            <div>
              {photos.length > 0 && (
                <span>{photos.length} images found</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImagePicker;